import { useCallback, useMemo, Dispatch, SetStateAction } from "react";
import { useGlobalDrawerStack, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import type {
  BaseEntity,
  SimpleTableConfig,
  EntityHooks,
  TableFilters,
  AdvancedFilters,
  PaginationState,
  SerializedViewState,
} from "../../UniversalCRMTable/types";
import type { BulkAction } from "../../UniversalCRMTable/types/shared";
import { useCrudHandlers } from "../handlers/useCrudHandlers";
import { usePaginationHandlers } from "../handlers/usePaginationHandlers";
import { useExportHandlers } from "../handlers/useExportHandlers";
import { useFilterContext } from "@/contexts/FilterContext";

interface UseUniversalEntityPageHandlersProps<T extends BaseEntity> {
  entityConfig: {
    entityName: string;
    config: SimpleTableConfig<T>;
    hooks: EntityHooks<T>;
  };
  uiState: {
    isMobile: boolean;
    canCreate: boolean;
  };
  dataState: {
    currentPage: number;
    setCurrentPage: (page: number) => void;
    data: any;
    setPagination: Dispatch<SetStateAction<PaginationState>>;
    refetch: () => void;
  };
  entityState: {
    editingItem: T | null;
    setEditingItem: (item: T | null) => void;
    deletingItem: T | null;
    setDeletingItem: (item: T | null) => void;
  };
  filterState: {
    filtersManuallyChanged: boolean;
    setSerializeFilters: (fn: () => SerializedViewState) => void;
    setApplyFilters: (
      fn: (filters: SerializedViewState, isManualSelection?: boolean) => void
    ) => void;
    setCurrentFilters: (filters: SerializedViewState) => void;
    setAdvancedFilters: (filters: AdvancedFilters) => void;
    setFilterOperator: (op: "AND" | "OR") => void;
    setTableFilters: (filters: TableFilters) => void;
    debouncedSearchTerm: string;
    customFilters: TableFilters;
    currentFilters: SerializedViewState;
    advancedFilters: AdvancedFilters;
    filterHelpers: any;
  };
  exportState: {
    isFetchingRef: React.MutableRefObject<boolean>;
    setExportProgress: (progress: number) => void;
    setShowExportModal: (show: boolean) => void;
    setIsExporting: (exporting: boolean) => void;
  };
}

/**
 * Event handlers orchestration for UniversalEntityPage
 * Consolidates all handler setup and creation
 */
export function useUniversalEntityPageHandlers<T extends BaseEntity>({
  entityConfig,
  uiState,
  dataState,
  entityState,
  filterState,
  exportState,
}: UseUniversalEntityPageHandlersProps<T>) {
  const { entityName, config, hooks } = entityConfig;
  const { isMobile, canCreate } = uiState;
  const { currentPage, setCurrentPage, data, setPagination, refetch } = dataState;
  const { editingItem, setEditingItem, deletingItem, setDeletingItem } = entityState;
  const {
    filtersManuallyChanged,
    setSerializeFilters,
    setApplyFilters,
    setCurrentFilters,
    setAdvancedFilters,
    setFilterOperator,
    setTableFilters,
    debouncedSearchTerm,
    customFilters,
    currentFilters,
    advancedFilters,
  } = filterState;
  const { isFetchingRef, setExportProgress, setShowExportModal, setIsExporting } = exportState;

  // Get filter helpers from FilterContext
  let filterContext;
  try {
    filterContext = useFilterContext<T>();
  } catch {
    filterContext = null;
  }
  const filterHelpers = filterContext?.helpers || {
    convertToGraphQLFilter: () => ({}),
    constructSearchFilter: () => ({}),
    convertAdvancedFiltersToGraphQL: () => ({}),
  };

  const globalModalStack = useGlobalModalStack();
  const drawerStack = useGlobalDrawerStack();

  // Pagination handlers
  const paginationHandlers = usePaginationHandlers(
    currentPage,
    setCurrentPage,
    data as { pageInfo?: { endCursor?: string | null } } | undefined,
    setPagination
  );
  const { handlePageChange, handlePageSizeChange } = paginationHandlers;

  // CRUD handlers
  const crudHandlers = useCrudHandlers(
    entityName,
    config,
    isMobile,
    hooks,
    globalModalStack,
    drawerStack,
    editingItem,
    setEditingItem,
    deletingItem,
    setDeletingItem,
    canCreate,
    refetch
  );
  const {
    handleCreateItem,
    handleEditItem,
    handleInlineEdit,
    handleDeleteItem,
    handleViewItem,
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
    handleImportItems,
    handleCancelDelete,
    bulkActions,
    handleBulkAction,
  } = crudHandlers;

  // Export handlers
  const exportHandlers = useExportHandlers(
    entityName,
    config,
    hooks,
    "all", // filterPreset - passed separately if needed
    null, // appUser - passed separately if needed
    debouncedSearchTerm,
    customFilters,
    // currentFilters is a fixed-key SerializedViewState; spread it into a plain
    // filter map (GraphQLFilter = Record<string, unknown>) for the export
    // pipeline, which flattens it via normalizeSerializedFilters at runtime.
    { ...currentFilters },
    advancedFilters,
    data,
    filterHelpers,
    isFetchingRef,
    setExportProgress,
    setShowExportModal,
    setIsExporting
  );
  const { handleExportWithScope, handleExportItems } = exportHandlers;

  // Saved views handlers - no longer needed with FilterContext
  // FilterContext handles all saved view logic via applyPreset()
  const handleSavedViewsReady = useCallback(() => {
    // This is now handled by FilterContext
    // No action needed here
  }, []);

  // Other handlers
  const handleComposeMessage = useCallback(() => {
    // This will be set by the component using this hook
    // Placeholder for now
  }, []);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  // Simplified - no infinite scrolling for now
  const loadMoreFunction = undefined;

  return {
    paginationHandlers,
    handlePageChange,
    handlePageSizeChange,
    crudHandlers,
    handleCreateItem,
    handleEditItem,
    handleInlineEdit,
    handleDeleteItem,
    handleViewItem,
    handleItemCreated,
    handleItemUpdated,
    handleItemDeleted,
    handleImportItems,
    handleCancelDelete,
    bulkActions,
    handleBulkAction,
    exportHandlers,
    handleExportWithScope,
    handleExportItems,
    handleSavedViewsReady,
    handleComposeMessage,
    handleRefetch,
    loadMoreFunction,
  };
}
